// Deep-links into the compare directory (/hearing-aids/compare).
//
// The directory renders client-side with pure in-memory faceting
// (use-directory-reducer.ts) and hydrates its filter state from these query
// params on mount (parseDirectoryQuery). Product pages and the hearing-aids
// dashboard build these URLs to link into the directory pre-filtered — which
// also keeps them on the real (200) route instead of the old page-router
// `/hearing-aids/compare/f/...` path grammar, for which this app has no route.
//
// Param names map 1:1 to the directory's array filter axes; values must match
// the grid's r.<axis> value space exactly (e.g. hardware = 'Telecoil',
// style = 'Behind the Ear', classification = 'OTC', tags = canonical English
// tag). Multiple values per axis are comma-separated. Values containing commas
// are not supported (none of the current axes have them).

export type FeatureMode = 'and' | 'or'

export const COMPARE_PATH = '/hearing-aids/compare'

// Every array-valued filter axis on DirectoryFilters (excluding `brand` and `featureMode`).
// Kept in sync with FilterArrayKey via a compile-time guard in
// use-directory-reducer.ts.
export const COMPARE_ARRAY_PARAMS = [
  'classification',
  'tags',
  'channels',
  'accessories',
  'hardware',
  'software',
  'loss',
  'style',
] as const

export type CompareArrayParam = (typeof COMPARE_ARRAY_PARAMS)[number]

export type CompareParams = Partial<Record<CompareArrayParam, string[]>> & {
  /** The single-valued brand axis (the grid's `r.brand` value, i.e. the brand
   *  slug such as "resound") — hydrated by parseDirectoryQuery like the array axes. */
  brand?: string
  featureMode?: FeatureMode
}

export const compareUrl = (params: CompareParams = {}): string => {
  const search = new URLSearchParams()
  if (params.brand) {
    search.set('brand', params.brand)
  }
  for (const key of COMPARE_ARRAY_PARAMS) {
    const values = params[key]
    if (values && values.length > 0) {
      search.set(key, values.join(','))
    }
  }
  if (params.featureMode) {
    search.set('featureMode', params.featureMode)
  }
  const qs = search.toString()
  return qs ? `${COMPARE_PATH}?${qs}` : COMPARE_PATH
}
